<template>
  <div class="overview">
    <div class="content">
      <div class="row">
        <div class="col-md-6 detail-info-box">
          <h1>Overview</h1>
          <h2>Non-CO₂Greenhouse Gases</h2>

          <div class="row">
            <div class="col-lg-7">
              <p>Non-CO₂ greenhouse gases are more potent than CO₂ (per unit weight) at trapping heat within the atmosphere. Global warming potential (GWP) is the factor that quantifies the heat trapping potential of each GHG relative to that of carbon dioxide (CO₂). For example, methane has a GWP value of 21 which means that each molecule of methane released into the atmosphere is 21 more times effective at trapping heat compared to an equivalent unit of CO₂. Additionally, some non-CO₂ GHGs can remain in the atmosphere for longer periods of time than CO₂.</p>
            </div>
            <div class="col-lg-5">
              <table class="table table-sm">
                <thead class="light-head">
                  <tr>
                    <th scope="col">Greenhouse Gas</th>
                    <th scope="col">GWP Factor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>CO₂</td>
                    <td>1</td>
                  </tr>
                  <tr>
                    <td>CH₄</td>
                    <td>25</td>
                  </tr>
                  <tr>
                    <td>N₂O</td>
                    <td>298</td>
                  </tr>
                  <tr>
                    <td>HFCs</td>
                    <td>124 - 14,800</td>
                  </tr>
                  <tr>
                    <td>NF₃</td>
                    <td>17,200</td>
                  </tr>
                  <tr>
                    <td>SF₆</td>
                    <td>22,800</td>
                  </tr>
                  <tr>
                    <td>PFCs</td>
                    <td>7,390 - 12,200</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2>Emission Projections and Mitigation Assessments</h2>
            <div class="tabs">
              <b-tabs content-class="mt-2">
                <b-tab title="Emission Projections" active>
                  <p>
                    Emission Projections
                    were generated using a combination of country-reported inventory data supplemented with EPA-estimated calculations consistent with inventory guidelines
                    of the Intergovernmental Panel on Climate Change (IPCC). Historical emission estimates were incorporated from country-reported data from 1990 through 2015, and emissions
                    were projected through 2050. The projections results are a “business-as-usual” (BAU) scenario with emission rates consistent with historical levels and do not include future effects of policy changes.
                  </p>

                  <div class="row">
                    <div class="col-sm-12">
                      <strong class="space-right-25">Explore Summary:</strong>
                      <button type="button" @click="openDialog2">Top 5 Emitters</button>
                    </div>
                  </div>
                </b-tab>
                <b-tab title="Mitigation Assessments">
                  <p>
                    Mitigation Assessments
                    were generated using a bottom-up, engineering cost approach that analyzed the costs of a wide range of mitigation technologies and incorporated them into an economic tool called a marginal abatement cost (MAC) curve. MACs provide information on the amount of emissions reductions that can be achieved as well as an estimate of the costs of implementing the GHG abatement measures.
                    Mitigation options represented in the MAC curves reduce emissions from the BAU scenario. Although emission and mitigation estimates are available through 2050.
                  </p>
                  <div class="row">
                    <div class="col-sm-12">
                      <strong class="space-right-25">Explore Summary:</strong>
                      <button
                        type="button"
                        @click="openDialog1"
                      >Emission Projections and Mitigation Scenarios</button>
                    </div>
                  </div>
                </b-tab>
              </b-tabs>
            </div>
          </div>
        </div>

        <div class="col-md-6 chartarea">
          <p class="center">
            <button
              class="active"
              id="gas"
              v-on:click="tab1 = true; tab2 = false; whichTab('gas');"
            >By Gas</button>
            <button
              class
              id="source"
              v-on:click="tab2 = true; tab1 = false; whichTab('source');"
            >By Source</button>
          </p>

          <div v-if="tab1">
            <Chart1 />
            <div class="row">
              <caption>
                <p>The EPA estimates that global non-CO₂ GHG emissions in 2015 totaled approximately 12,010 MtCO₂e. When added to a global CO₂ emission estimate for 2015 of approximately 36,000 MtCO₂e, anthropogenic non-CO₂ emissions represent 25% of the global GHG emissions emitted annually on a CO₂ equivalent basis in 2015.</p>
              </caption>
            </div>
          </div>

          <div v-if="tab2">
            <p class="center" style="border-top: 1px solid #eee; padding-top: 20px;">
              <button @click="select('btn1')" class="active" id="2020">2020</button>
              <button @click="select('btn2')" id="2050">2050</button>
            </p>

            <div v-if="btn1">
              <Chart2 />
            </div>
            <div v-if="btn2">
              <Chart2 />
            </div>

            <div class="row">
              <caption>
                <p>
                  For this report, emission sources were grouped into four economic sectors: energy, industrial processes, agriculture, and waste.
                  Although CO2 emissions are concentrated in the energy sector, agriculture accounts for the largest share of non-CO2 emissions throughout the time series.
                  Emissions from the industrial processes and waste sectors are projected to grow at the fastest rates between 2020 and 2050.
                </p>
              </caption>
            </div>
          </div>
        </div>
      </div>
    </div>

    <a11y-dialog
      id="main-dialog1"
      app-root="#app"
      dialog-root="#dialog-root"
      @dialog-ref="assignDialogReference1"
      :class-names="{}"
      :disable-native="useFallback"
    >
      <div>
        <OverviewPopup />
      </div>
    </a11y-dialog>

    <a11y-dialog
      id="main-dialog2"
      app-root="#app"
      dialog-root="#dialog-root"
      @dialog-ref="assignDialogReference2"
      :class-names="{}"
      :disable-native="useFallback"
    >
      <div>
        <OverviewPopup2 />
      </div>
    </a11y-dialog>
  </div>
</template>

<script>
import axios from "axios";
import $ from "jquery";
import OverviewPopup from "@/components/OverviewPopup";
import OverviewPopup2 from "@/components/OverviewPopup2";
import Chart1 from "./ChartOverview";
import Chart2 from "./ChartOverview2";
import { EventBus } from "@/event-bus.js";

var topLevel2015 = [],
  topLevel2050 = [],
  secondLevelData12015 = [],
  secondLevelData12050 = [],
  secondLevelData22015 = [],
  secondLevelData22050 = [],
  secondLevelData32015 = [],
  secondLevelData32050 = [],
  secondLevelData42015 = [],
  secondLevelData42050 = [];

export default {
  name: "OverviewScreen",
  components: {
    Chart1,
    OverviewPopup,
    OverviewPopup2,
    Chart2
  },
  props: {
    msg: String
  },
  data() {
    return {
      tab1: true,
      tab2: false,
      btn1: true,
      btn2: false,
      dialog1: null,
      dialog2: null
    };
  },

  mounted() {
    console.log("hi");
    axios
      .get(
        "https://appdev4.erg.com/nonco2/reports/static/nonco2-2015-2050formated.json"
      )
      .then(response => {
        this.items = response.data.results[0].items;
        var sectorName, categoryName;

        for (var i = 0; i < this.items.length; i++) {
          if (this.items[i].data_year === 2015) {
            sectorName = this.items[i].sector.toLowerCase();
            categoryName = this.items[i].category_details.toLowerCase();

            if (sectorName === categoryName) {
              topLevel2015.push({
                name: this.items[i].category_details,
                y: this.items[i].emis_data,
                drilldown: sectorName
              });
            }

            if (
              sectorName === "agriculture" &&
              this.items[i].category_id !== 100
            ) {
              secondLevelData12015.push([
                this.items[i].category_details,
                this.items[i].emis_data
              ]);
            }

            if (sectorName === "energy" && this.items[i].category_id !== 200) {
              secondLevelData22015.push([
                this.items[i].category_details,
                this.items[i].emis_data
              ]);
            }

            if (
              sectorName === "industrial processes" &&
              this.items[i].category_id !== 300
            ) {
              secondLevelData32015.push([
                this.items[i].category_details,
                this.items[i].emis_data
              ]);
            }

            if (sectorName === "waste" && this.items[i].category_id !== 400) {
              secondLevelData42015.push([
                this.items[i].category_details,
                this.items[i].emis_data
              ]);
            }
          }

          if (this.items[i].data_year === 2050) {
            sectorName = this.items[i].sector.toLowerCase();
            categoryName = this.items[i].category_details.toLowerCase();

            if (sectorName === categoryName) {
              topLevel2050.push({
                name: this.items[i].category_details,
                y: this.items[i].emis_data,
                drilldown: sectorName
              });
            }

            if (
              sectorName === "agriculture" &&
              this.items[i].category_id !== 100
            ) {
              secondLevelData12050.push([
                this.items[i].category_details,
                this.items[i].emis_data
              ]);
            }

            if (sectorName === "energy" && this.items[i].category_id !== 200) {
              secondLevelData22050.push([
                this.items[i].category_details,
                this.items[i].emis_data
              ]);
            }

            if (
              sectorName === "industrial processes" &&
              this.items[i].category_id !== 300
            ) {
              secondLevelData32050.push([
                this.items[i].category_details,
                this.items[i].emis_data
              ]);
            }

            if (sectorName === "waste" && this.items[i].category_id !== 400) {
              secondLevelData42050.push([
                this.items[i].category_details,
                this.items[i].emis_data
              ]);
            }
          }
        }
      })
      .catch(error => {
        console.log(error);
        this.errored = true;
      })
      .finally(() => (this.loading = false));
  },

  methods: {
    whichTab: function(tab) {
      if (tab === "gas") {
        $("#gas").addClass("active");
        $("#source").removeClass("active");
      }
      if (tab === "source") {
        console.log("source clicked");
        $("#source").addClass("active");
        $("#gas").removeClass("active");
      }
    },
    openDialog1() {
      if (this.dialog1) {
        this.dialog1.show();
        //app.$refs.fullpage.setAllowScrolling(false);
        //this.$root.$refs.fullpage.api.setAllowScrolling(false);
        //this.fn.fullpage.setAllowScrolling(false);
        //console.log(this.$parent.$refs.fullpage);
      }
    },
    openDialog2() {
      if (this.dialog2) {
        this.dialog2.show();
      }
    },
    assignDialogReference1(dialog1) {
      this.dialog1 = dialog1;
    },
    assignDialogReference2(dialog2) {
      this.dialog2 = dialog2;
    },

    handler() {
      var args = arguments;
      for (var arg of args) {
        if (arg instanceof Function) {
          arg();
        }
      }
    },

    select(elem) {
     // alert("selection " + elem);
      this.currentView = elem;
      //this.activate(elem);
      if (this.currentView == "btn1") {
        EventBus.$emit("btn-clicked", {
          newData: topLevel2015,
          newSecondLevelData1: secondLevelData12015,
          newSecondLevelData2: secondLevelData22015,
          newSecondLevelData3: secondLevelData32015,
          newSecondLevelData4: secondLevelData42015
        });

        $("#2020").addClass("active");
        $("#2050").removeClass("active");
      }
      if (this.currentView == "btn2") {
        EventBus.$emit("btn-clicked", {
          newData: topLevel2050,
          newSecondLevelData1: secondLevelData12050,
          newSecondLevelData2: secondLevelData22050,
          newSecondLevelData3: secondLevelData32050,
          newSecondLevelData4: secondLevelData42050
        });
        $("#2050").addClass("active");
        $("#2020").removeClass("active");
      }
    }
  },

  computed: {
    useFallback() {
      return typeof window.HTMLDialogElement !== "function";
    }
  }
};
</script>

<style scoped>
h1 {
  font-weight: normal;
  font-size: 2rem;
  margin-bottom: 1rem;
}
.content {
  padding-left: 150px;
  padding-right: 150px;
}
.table-sm {
  min-width: 203px !important;
}
.table-sm th:last-child {
  min-width: 120px !important;
}
@media all and (max-width: 1280px) {
  .content {
    padding-left: 80px;
    padding-right: 80px;
  }
}
@media all and (max-width: 980px) {
  .content {
    margin-top: 70px;
    padding-left: 50px;
    padding-right: 50px;
  }
}
@media all and (max-width: 736px) {
  h1 {
    font-size: 1.5rem;
  }
}
/* Create two unequal columns that floats next to each other */
.column {
  float: left;
  padding: 15px;
}
.left {
  width: 65%;
}
.right {
  width: 35%;
}
/* Clear floats after the columns */
.row:after {
  content: "";
  display: table;
  clear: both;
}
</style>
